import { useEffect, useRef, useState } from 'react'
import type { TransactionType } from '@zeni/shared'
import { useLanguage } from '../contexts/LanguageContext'
import { useAmountFormat } from '../contexts/AmountUnitContext'
import { btnAccentSm, btnPrimarySm, btnSecondarySm } from '../lib/ui'

interface Props {
  /** 円単位の整数を表す文字列。未指定は空文字 */
  min: string
  max: string
  /** 対象の種別。空配列は全種別 */
  types: TransactionType[]
  onChange: (min: string, max: string, types: TransactionType[]) => void
  className?: string
}

const sameTypes = (a: TransactionType[], b: TransactionType[]) =>
  a.length === b.length && a.every((v) => b.includes(v))

/** 入力欄の文字列を円単位の整数文字列に正規化する。数値でなければ空文字 */
const normalize = (s: string) => {
  const cleaned = s.replace(/[,\s]/g, '')
  if (!cleaned) return ''
  const n = Number(cleaned)
  if (!Number.isFinite(n) || n < 0) return ''
  return String(Math.floor(n))
}

export function AmountRangePicker({ min, max, types, onChange, className }: Props) {
  const { t } = useLanguage()
  const { formatJPY } = useAmountFormat()

  const [open, setOpen] = useState(false)
  // 適用するまで親には渡さないので、開いている間の入力はここで持つ
  const [draftMin, setDraftMin] = useState(min)
  const [draftMax, setDraftMax] = useState(max)
  const [draftTypes, setDraftTypes] = useState<TransactionType[]>(types)

  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  // 閉じたら入力途中の値は破棄し、確定済みの条件に戻す
  useEffect(() => {
    if (open) return
    setDraftMin(min)
    setDraftMax(max)
    setDraftTypes(types)
  }, [open, min, max, types])

  // 収入・支出はここで切り替えるが、振替はこの UI に出ないので
  // チェックを触った時点で対象から外す（残すと意図しない絞り込みになる）
  const toggleType = (type: 'income' | 'expense') => {
    setDraftTypes((prev) => {
      const base = prev.filter((v) => v !== 'transfer')
      return base.includes(type) ? base.filter((v) => v !== type) : [...base, type]
    })
  }

  const commit = (nextMin: string, nextMax: string, nextTypes: TransactionType[]) => {
    let lower = normalize(nextMin)
    let upper = normalize(nextMax)
    // 下限のほうが大きいと必ず 0 件になるので、入れ替えて意図どおりの幅にする
    if (lower && upper && Number(lower) > Number(upper)) [lower, upper] = [upper, lower]
    setOpen(false)
    if (lower !== min || upper !== max || !sameTypes(nextTypes, types)) onChange(lower, upper, nextTypes)
  }

  const presets: { label: string; range: [string, string] }[] = [
    { label: t('amount_range_preset_under_10k'), range: ['', '10000'] },
    { label: t('amount_range_preset_10k_50k'), range: ['10000', '50000'] },
    { label: t('amount_range_preset_50k_100k'), range: ['50000', '100000'] },
    { label: t('amount_range_preset_over_100k'), range: ['100000', ''] },
  ]

  // チェックの状態だけでは読み取りにくい「どちらも」「振替のみ」を言葉で補う
  const hasIncome = draftTypes.includes('income')
  const hasExpense = draftTypes.includes('expense')
  let targetNote = ''
  if (hasIncome && hasExpense) targetNote = t('amount_range_target_both')
  else if (!hasIncome && !hasExpense) {
    targetNote = draftTypes.includes('transfer')
      ? t('amount_range_target_transfer_only')
      : t('amount_range_target_all')
  }

  const triggerLabel = min || max
    ? `${min ? formatJPY(Number(min)) : '…'} – ${max ? formatJPY(Number(max)) : '…'}`
    : t('amount_range_placeholder')

  const inputClass =
    'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-right dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'

  return (
    <div ref={containerRef} className={`relative ${className ?? ''}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={`flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors dark:bg-gray-800 dark:text-gray-100 ${
          open
            ? 'border-indigo-500 ring-1 ring-indigo-500 dark:border-indigo-500'
            : 'border-gray-200 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700'
        }`}
      >
        <span aria-hidden="true" className="text-gray-400 dark:text-gray-500">💴</span>
        <span className={min || max ? '' : 'text-gray-400 dark:text-gray-500'}>{triggerLabel}</span>
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={t('amount_range_placeholder')}
          className="absolute left-0 top-full z-30 mt-2 w-[19.5rem] rounded-xl border border-gray-200 bg-white p-4 shadow-lg sm:left-auto sm:right-0 dark:border-gray-700 dark:bg-gray-900"
        >
          <div className="flex items-end gap-2">
            <label className="flex-1">
              <span className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
                {t('amount_range_min')}
              </span>
              <input
                type="number"
                min="0"
                step="1"
                inputMode="numeric"
                value={draftMin}
                autoFocus
                onChange={(e) => setDraftMin(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') commit(draftMin, draftMax, draftTypes) }}
                placeholder="0"
                className={inputClass}
              />
            </label>
            <span className="pb-2 text-sm text-gray-400 dark:text-gray-500">–</span>
            <label className="flex-1">
              <span className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">
                {t('amount_range_max')}
              </span>
              <input
                type="number"
                min="0"
                step="1"
                inputMode="numeric"
                value={draftMax}
                onChange={(e) => setDraftMax(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') commit(draftMin, draftMax, draftTypes) }}
                placeholder={t('amount_range_no_limit')}
                className={inputClass}
              />
            </label>
          </div>

          <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">{t('amount_range_hint')}</p>

          <fieldset className="mt-3">
            <legend className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400">
              {t('amount_range_target')}
            </legend>
            <div className="flex flex-wrap items-center gap-4">
              {(['expense', 'income'] as const).map((type) => (
                <label key={type} className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-200">
                  <input
                    type="checkbox"
                    checked={draftTypes.includes(type)}
                    onChange={() => toggleType(type)}
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800"
                  />
                  {t(type === 'expense' ? 'type_expense' : 'type_income')}
                </label>
              ))}
              <span className="text-xs text-gray-400 dark:text-gray-500">{targetNote}</span>
            </div>
          </fieldset>

          <div className="mt-3 flex flex-wrap gap-2">
            {presets.map((preset) => (
              <button
                key={preset.label}
                type="button"
                onClick={() => commit(preset.range[0], preset.range[1], draftTypes)}
                className={btnSecondarySm}
              >
                {preset.label}
              </button>
            ))}
          </div>

          <div className="mt-3 flex items-center gap-2 border-t border-gray-100 pt-3 dark:border-gray-800">
            <button
              type="button"
              onClick={() => commit('', '', [])}
              disabled={!min && !max && !draftMin && !draftMax && types.length === 0 && draftTypes.length === 0}
              className={btnAccentSm}
            >
              {t('amount_range_clear')}
            </button>
            <button
              type="button"
              onClick={() => commit(draftMin, draftMax, draftTypes)}
              className={`${btnPrimarySm} ml-auto`}
            >
              {t('amount_range_apply')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
